// src/hooks/useActas.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "./useAuth";
import { getMeetingSessionsApi } from "../api/meetingSession";
import { generarActaApi } from "../api/meetingSessionActa";
import { fetchActaVersions } from "../api/meetingSessionActaVersion";
import {
  generarActasReunionApi,
  descargarWordCompletoApi,
} from "../api/meetingActas";
import { generarActaAutoApi } from "../api/meetings";

export function useActas() {
  const { auth } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [batchLoadingMeetingId, setBatchLoadingMeetingId] = useState(null);
  const [error, setError] = useState(null);

  /** Carga todas las sesiones del usuario */
  const fetchSessions = useCallback(async () => {
    if (!auth?.token) { setSessions([]); return []; }
    setLoading(true);
    setError(null);
    try {
      const list = await getMeetingSessionsApi(auth.token);
      const enhanced = (list || []).map((s) => ({
        ...s,
        meeting_id: s.meeting,
        meeting_title: s.meeting_title || "Sin título",
      }));
      setSessions(enhanced);
      return enhanced;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  /** Derivar reuniones únicas desde las sesiones */
  const meetings = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      if (!map.has(s.meeting_id)) {
        map.set(s.meeting_id, { id: s.meeting_id, title: s.meeting_title });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.title).localeCompare(String(b.title))
    );
  }, [sessions]);

  /** Generar acta para una sesión */
  const generateActa = useCallback(
    async (sessionId) => {
      if (!auth?.token) throw new Error("No hay token de autenticación");
      setGeneratingId(sessionId);
      setError(null);
      try {
        const resp = await generarActaApi(sessionId, auth.token);
        return resp; // { msg, acta_version_id }
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setGeneratingId(null);
      }
    },
    [auth?.token]
  );

  /** Generar actas para TODAS las sesiones de una reunión */
  const generateAllForMeeting = useCallback(
    async (meetingId) => {
      if (!auth?.token) throw new Error("No hay token de autenticación");
      setBatchLoadingMeetingId(meetingId);
      setError(null);
      try {
        const resp = await generarActasReunionApi(meetingId, auth.token);
        await fetchSessions(); // refrescar estados acta_status
        return resp; // { meeting_id, results: [...] }
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setBatchLoadingMeetingId(null);
      }
    },
    [auth?.token, fetchSessions]
  );

  /** Descargar Word unificado de la reunión */
  const downloadWordForMeeting = useCallback(
    async (meetingId, filename = null) => {
      if (!auth?.token) throw new Error("No hay token de autenticación");
      const blob = await descargarWordCompletoApi(meetingId, auth.token);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || `Acta_Reunion_${meetingId}_COMPLETA.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    },
    [auth?.token]
  );

  /** (Opcional) versiones de acta por sesión */
  const fetchVersionsBySession = useCallback(
    async (sessionId) => {
      if (!auth?.token) return [];
      try {
        const versions = await fetchActaVersions(sessionId, auth.token);
        return versions;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [auth?.token]
  );

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const generateActaAuto = useCallback(
    async (meetingId) => {
      if (!auth?.token) throw new Error("No hay token de autenticación");
      setBatchLoadingMeetingId(meetingId);
      setError(null);
      try {
        const resp = await generarActaAutoApi(meetingId, auth.token);
        await fetchSessions(); // refrescar estados
        return resp; // { type, preview, docx_url, ... }
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setBatchLoadingMeetingId(null);
      }
    },
    [auth?.token, fetchSessions]
  );

  return {
    sessions,
    meetings,
    loading,
    generatingId,
    batchLoadingMeetingId,
    error,
    // actions
    reload: fetchSessions,
    generateActa,
    generateAllForMeeting,
    downloadWordForMeeting,
    fetchVersionsBySession,
    generateActaAuto,
  };
}
